import { useState } from "react";
import { Check } from "lucide-react";
import { cn } from "@/lib/utils";
import { KIT } from "@/constants/testIds";

const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

/** Day index: one line of week chips, then one row per day of that week.
 *  Reads straight down: which day, which weekday, whether it is done, and which one is next. */
export const WeekIndex = ({ total = 0, current, completed = [], authored, days = [], onSelect }) => {
  const weeks = Math.ceil(total / 7);
  const [week, setWeek] = useState(Math.min(Math.ceil((current || 1) / 7), weeks) || 1);
  const start = (week - 1) * 7 + 1;
  const dayList = Array.from({ length: Math.max(Math.min(7, total - start + 1), 0) }, (_, i) => start + i);
  const mealsFor = (n) => days.find((d) => d.day_number === n)?.meals ?? [];

  return (
    <div data-testid={KIT.weekIndex} className="space-y-5">
      <div className="-mx-1 flex gap-2 overflow-x-auto px-1 pb-1">
        {Array.from({ length: weeks }, (_, w) => (
          <button
            key={w}
            data-testid={KIT.weekGroup(w + 1)}
            onClick={() => setWeek(w + 1)}
            aria-current={week === w + 1 ? "true" : undefined}
            className={cn(
              "ps-tap min-h-[40px] shrink-0 rounded-full px-5 text-[13.5px] font-bold transition-colors",
              week === w + 1
                ? "bg-brand-tealDeep text-white"
                : "bg-white text-brand-teal hover:bg-secondary"
            )}
          >
            Week {w + 1}
          </button>
        ))}
      </div>

      <ul className="space-y-3">
        {dayList.map((n) => {
          const isDone = completed.includes(n);
          const isCurrent = n === current;
          const hasContent = !authored || authored.has(n);
          const meals = mealsFor(n);
          return (
            <li key={n}>
              <button
                data-testid={KIT.weekDay(n)}
                onClick={() => onSelect?.(n)}
                aria-current={isCurrent ? "true" : undefined}
                aria-label={`Day ${n}`}
                className={cn(
                  "ps-tap flex w-full items-center gap-4 rounded-[20px] bg-white px-4 py-4 text-left shadow-card transition-colors",
                  !hasContent && "opacity-50"
                )}
              >
                <span
                  className={cn(
                    "grid h-[52px] w-[52px] shrink-0 place-items-center rounded-[16px] font-display text-[17px] font-bold",
                    meals.length > 0 && "hidden",
                    isCurrent ? "bg-brand-peach text-brand-orange" : "bg-secondary text-brand-teal"
                  )}
                >
                  {n}
                </span>
                {meals.length > 0 && (
                  <span className="flex shrink-0 items-center">
                    {meals.slice(0, 3).map((meal, i) => (
                      <span
                        key={meal.slot}
                        className={cn(
                          "block h-10 w-10 shrink-0 overflow-hidden rounded-[12px] ring-2 ring-white",
                          i > 0 && "-ml-3"
                        )}
                      >
                        {meal.image ? (
                          <img
                            src={meal.image}
                            alt=""
                            loading="lazy"
                            decoding="async"
                            className="h-full w-full object-cover"
                          />
                        ) : (
                          <span className="grid h-full w-full place-items-center bg-secondary font-display text-[12px] font-bold text-brand-teal/50">
                            {meal.title?.[0]}
                          </span>
                        )}
                      </span>
                    ))}
                  </span>
                )}
                <span className="min-w-0 flex-1">
                  <span
                    className={cn(
                      "block font-display text-[16.5px] font-bold leading-snug",
                      isCurrent ? "text-brand-orange" : "text-brand-teal"
                    )}
                  >
                    Day {n} · {WEEKDAYS[(n - 1) % 7]}
                  </span>
                  <span className="mt-0.5 block text-[13.5px] leading-[1.45] text-muted-foreground">
                    {meals.length
                      ? meals.map((m) => m.title).join(", ")
                      : hasContent
                        ? "Breakfast, lunch and dinner"
                        : "No content yet"}
                  </span>
                </span>
                {isDone ? (
                  <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-secondary text-brand-green">
                    <Check className="h-4 w-4" strokeWidth={3} />
                  </span>
                ) : isCurrent ? (
                  <span className="shrink-0 text-[11.5px] font-bold uppercase tracking-[0.1em] text-brand-orange">
                    Next
                  </span>
                ) : null}
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
};
